import { useState, useEffect } from 'react';
import { Package, Plus, Search, Filter, AlertTriangle, TrendingDown, TrendingUp, Edit, Trash2, X, Download, Upload, Barcode, MapPin } from 'lucide-react';
import { IconTile, TINT } from '../components/ui/IconTile';
import { toast } from 'sonner';
import { generateInventoryReportPDF } from '../utils/PDFGenerator';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all, low, out
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    location: '',
    quantity: 0,
    minQuantity: 0,
    maxQuantity: 0,
    unit: 'units',
    cost: 0,
    supplier: '',
    notes: '',
    image: ''
  });

  const [categoryForm, setCategoryForm] = useState({ name: '', color: '#0073ea' });
  const [locationForm, setLocationForm] = useState({ name: '', address: '' });

  const units = ['units', 'bottles', 'boxes', 'gallons', 'liters', 'rolls', 'bags', 'cases'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const itemsData = JSON.parse(localStorage.getItem('maidly_inventory') || '[]');
    setItems(itemsData);
    
    const categoriesData = JSON.parse(localStorage.getItem('maidly_inventory_categories') || '[]');
    if (categoriesData.length === 0) {
      // Initialize default categories
      const defaultCategories = [
        { id: '1', name: 'Cleaning Supplies', color: '#0073ea' },
        { id: '2', name: 'Equipment', color: '#1e3a5f' },
        { id: '3', name: 'Chemicals', color: '#f5576c' },
        { id: '4', name: 'Tools', color: '#00c875' },
        { id: '5', name: 'Safety Gear', color: '#fdab3d' }
      ];
      localStorage.setItem('maidly_inventory_categories', JSON.stringify(defaultCategories));
      setCategories(defaultCategories);
    } else {
      setCategories(categoriesData);
    }

    const locationsData = JSON.parse(localStorage.getItem('maidly_inventory_locations') || '[]');
    if (locationsData.length === 0) {
      // Initialize default locations
      const defaultLocations = [
        { id: '1', name: 'Main Warehouse', address: '123 Storage St' },
        { id: '2', name: 'Vehicle 1', address: 'Company Van' },
        { id: '3', name: 'Office Storage', address: 'Office Building' }
      ];
      localStorage.setItem('maidly_inventory_locations', JSON.stringify(defaultLocations));
      setLocations(defaultLocations);
    } else {
      setLocations(locationsData);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingItem) {
      const updated = items.map(item => 
        item.id === editingItem.id ? { ...item, ...formData, updatedAt: new Date().toISOString() } : item
      );
      setItems(updated);
      localStorage.setItem('maidly_inventory', JSON.stringify(updated));
      toast.success('Item updated successfully');
    } else {
      const newItem = {
        ...formData,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const updated = [...items, newItem];
      setItems(updated);
      localStorage.setItem('maidly_inventory', JSON.stringify(updated));
      toast.success('Item added successfully');
    }
    
    resetForm();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const updated = items.filter(item => item.id !== id);
      setItems(updated);
      localStorage.setItem('maidly_inventory', JSON.stringify(updated));
      toast.success('Item deleted');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sku: item.sku,
      category: item.category,
      location: item.location,
      quantity: item.quantity,
      minQuantity: item.minQuantity,
      maxQuantity: item.maxQuantity,
      unit: item.unit,
      cost: item.cost,
      supplier: item.supplier,
      notes: item.notes,
      image: item.image || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      name: '',
      sku: '',
      category: '',
      location: '',
      quantity: 0,
      minQuantity: 0,
      maxQuantity: 0,
      unit: 'units',
      cost: 0,
      supplier: '',
      notes: '',
      image: ''
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
        toast.success('Image uploaded');
      };
      reader.onerror = () => {
        toast.error('Error uploading image');
      };
      reader.readAsDataURL(file);
    }
  };

  const addCategory = () => {
    if (!categoryForm.name) {
      toast.error('Please enter category name');
      return;
    }
    const newCategory = {
      id: Date.now().toString(),
      name: categoryForm.name,
      color: categoryForm.color
    };
    const updated = [...categories, newCategory];
    setCategories(updated);
    localStorage.setItem('maidly_inventory_categories', JSON.stringify(updated));
    setCategoryForm({ name: '', color: '#0073ea' });
    setShowCategoryModal(false);
    toast.success('Category added');
  };

  const addLocation = () => {
    if (!locationForm.name) {
      toast.error('Please enter location name');
      return;
    }
    const newLocation = {
      id: Date.now().toString(),
      name: locationForm.name,
      address: locationForm.address
    };
    const updated = [...locations, newLocation];
    setLocations(updated);
    localStorage.setItem('maidly_inventory_locations', JSON.stringify(updated));
    setLocationForm({ name: '', address: '' });
    setShowLocationModal(false);
    toast.success('Location added');
  };

  const getStatusBadge = (item) => {
    if (item.quantity === 0) {
      return <span className="badge badge-danger">Out of Stock</span>;
    } else if (item.quantity <= item.minQuantity) {
      return <span className="badge badge-warning">Low Stock</span>;
    } else if (item.quantity >= item.maxQuantity) {
      return <span className="badge badge-info">Overstocked</span>;
    } else {
      return <span className="badge badge-success">In Stock</span>;
    }
  };

  const exportToCSV = () => {
    // Format items for PDF
    const formattedItems = filteredItems.map(item => ({
      name: item.name,
      category: categories.find(c => c.id === item.category)?.name || 'N/A',
      quantity: item.quantity,
      unitPrice: item.cost || 0,
      status: item.quantity === 0 ? 'Out of Stock' : item.quantity <= item.minQuantity ? 'Low Stock' : 'In Stock'
    }));

    // Calculate summary
    const totalValue = filteredItems.reduce((sum, item) => sum + (item.quantity * (item.cost || 0)), 0);
    const lowStock = filteredItems.filter(item => item.quantity > 0 && item.quantity <= item.minQuantity).length;
    const outOfStock = filteredItems.filter(item => item.quantity === 0).length;

    generateInventoryReportPDF(formattedItems, {
      totalItems: filteredItems.length,
      totalValue: totalValue,
      lowStock: lowStock,
      outOfStock: outOfStock
    });
    
    toast.success('PDF Report downloaded!');
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesLocation = filterLocation === 'all' || item.location === filterLocation;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'low' && item.quantity <= item.minQuantity) ||
                         (filterStatus === 'out' && item.quantity === 0);
    return matchesSearch && matchesCategory && matchesLocation && matchesStatus;
  });

  const totalValue = items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.cost || 0)), 0);
  const lowStockCount = items.filter(item => item.quantity <= item.minQuantity && item.quantity > 0).length;
  const outOfStockCount = items.filter(item => item.quantity === 0).length;

  return (
    <div data-testid="inventory-page" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IconTile icon={Package} tint={TINT.inventory} size={44} />
            Inventory Management
          </h1>
          <p style={{ color: '#6b7280' }}>Track supplies, equipment, and materials in real-time</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Category — purple outline */}
          <button
            onClick={() => setShowCategoryModal(true)}
            data-testid="inventory-category-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#a855f70d';
              e.currentTarget.style.borderColor = '#a855f766';
              e.currentTarget.style.color = TINT.employees;
              e.currentTarget.style.boxShadow = '0 6px 16px #a855f733';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Plus} tint={TINT.employees} size={30} />
            Category
          </button>

          {/* Location — orange outline (matches Scheduling tint) */}
          <button
            onClick={() => setShowLocationModal(true)}
            data-testid="inventory-location-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f973160d';
              e.currentTarget.style.borderColor = '#f9731666';
              e.currentTarget.style.color = TINT.scheduling;
              e.currentTarget.style.boxShadow = '0 6px 16px #f9731633';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={MapPin} tint={TINT.scheduling} size={30} />
            Location
          </button>

          {/* Export — green outline (matches Invoices tint) */}
          <button
            onClick={exportToCSV}
            data-testid="inventory-export-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem',
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#374151',
              transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#22c55e0d';
              e.currentTarget.style.borderColor = '#22c55e66';
              e.currentTarget.style.color = TINT.invoices;
              e.currentTarget.style.boxShadow = '0 6px 16px #22c55e33';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Download} tint={TINT.invoices} size={30} />
            Export
          </button>

          {/* Add Item — purple primary (matches Inventory sidebar tint) */}
          <button
            onClick={() => setShowModal(true)}
            data-testid="inventory-add-item-button"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.55rem 1rem 0.55rem 0.55rem',
              background: TINT.inventory, color: '#fff',
              border: 'none', borderRadius: 12,
              fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
              boxShadow: `0 6px 16px ${TINT.inventory}55`,
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              letterSpacing: 0.1,
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#7c3aed';
              e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.inventory}88`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = TINT.inventory;
              e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.inventory}55`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
            Add Item
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        <div className="card" style={{ background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Items</p>
              <p style={{ fontSize: '2rem', fontWeight: 700 }}>{items.length}</p>
            </div>
            <Package size={40} style={{ opacity: 0.8 }} />
          </div>
        </div>

        <div className="card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Total Value</p>
              <p style={{ fontSize: '2rem', fontWeight: 700 }}>${totalValue.toFixed(2)}</p>
            </div>
            <TrendingUp size={40} style={{ opacity: 0.8 }} />
          </div>
        </div>

        <div className="card" style={{ background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)', color: '#2d3436' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: '0.5rem' }}>Low Stock</p>
              <p style={{ fontSize: '2rem', fontWeight: 700 }}>{lowStockCount}</p>
            </div>
            <AlertTriangle size={40} style={{ opacity: 0.8 }} />
          </div>
        </div>

        <div className="card" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: '#2d3436' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: '0.5rem' }}>Out of Stock</p>
              <p style={{ fontSize: '2rem', fontWeight: 700 }}>{outOfStockCount}</p>
            </div>
            <TrendingDown size={40} style={{ opacity: 0.8 }} />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card" style={{ marginBottom: '2rem', padding: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input"
              style={{ paddingLeft: '40px' }}
            />
          </div>
          
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="input">
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} className="input">
            <option value="all">All Locations</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>

          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input">
            <option value="all">All Status</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <table className="table">
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              <th>Image</th>
              <th>SKU</th>
              <th>Name</th>
              <th>Category</th>
              <th>Location</th>
              <th>Quantity</th>
              <th>Min/Max</th>
              <th>Cost</th>
              <th>Total Value</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                  <Package size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>No items found. Add your first inventory item to get started.</p>
                </td>
              </tr>
            ) : (
              filteredItems.map(item => {
                const category = categories.find(c => c.id === item.category);
                const location = locations.find(l => l.id === item.location);
                return (
                  <tr key={item.id}>
                    <td>
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.name}
                          style={{ 
                            width: '50px', 
                            height: '50px', 
                            objectFit: 'cover', 
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                          }}
                        />
                      ) : (
                        <div style={{ 
                          width: '50px', 
                          height: '50px', 
                          background: '#f3f4f6', 
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #e5e7eb'
                        }}>
                          <Package size={24} color="#9ca3af" />
                        </div>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{item.sku}</td>
                    <td style={{ fontWeight: 600 }}>{item.name}</td>
                    <td>
                      {category && (
                        <span style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          background: category.color + '20',
                          color: category.color
                        }}>
                          {category.name}
                        </span>
                      )}
                    </td>
                    <td>{location?.name || '-'}</td>
                    <td>
                      <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                        {item.quantity} {item.unit}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {item.minQuantity} / {item.maxQuantity}
                    </td>
                    <td>${(item.cost || 0).toFixed(2)}</td>
                    <td style={{ fontWeight: 600 }}>${((item.quantity || 0) * (item.cost || 0)).toFixed(2)}</td>
                    <td>{getStatusBadge(item)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button
                          onClick={() => handleEdit(item)}
                          data-testid={`inventory-edit-${item.id}`}
                          title="Edit item"
                          aria-label="Edit item"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            background: '#3b82f61F',
                            color: '#3b82f6',
                            border: 'none',
                            borderRadius: 9,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.15s ease, transform 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#3b82f633';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#3b82f61F';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <Edit size={16} strokeWidth={2.25} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          data-testid={`inventory-delete-${item.id}`}
                          title="Delete item"
                          aria-label="Delete item"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            background: '#ef44441F',
                            color: '#ef4444',
                            border: 'none',
                            borderRadius: 9,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.15s ease, transform 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#ef444433';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#ef44441F';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <Trash2 size={16} strokeWidth={2.25} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Item Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
              <button onClick={resetForm} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Item Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input"
                    placeholder="e.g., All-Purpose Cleaner"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>SKU *</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    required
                    className="input"
                    placeholder="e.g., CLN-001"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="input"
                  >
                    <option value="">Select category...</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Location *</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="input"
                  >
                    <option value="">Select location...</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Current Quantity *</label>
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) })}
                    required
                    min="0"
                    step="1"
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Unit</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="input"
                  >
                    {units.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Min Quantity (Reorder Point)</label>
                  <input
                    type="number"
                    value={formData.minQuantity}
                    onChange={(e) => setFormData({ ...formData, minQuantity: parseFloat(e.target.value) })}
                    min="0"
                    step="1"
                    className="input"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Max Quantity</label>
                  <input
                    type="number"
                    value={formData.maxQuantity}
                    onChange={(e) => setFormData({ ...formData, maxQuantity: parseFloat(e.target.value) })}
                    min="0"
                    step="1"
                    className="input"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Unit Cost</label>
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                    min="0"
                    step="0.01"
                    className="input"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Supplier</label>
                  <input
                    type="text"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    className="input"
                    placeholder="e.g., ABC Supplies"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input"
                  style={{ minHeight: '80px' }}
                  placeholder="Additional information..."
                />
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Item Image</label>
                
                {formData.image && (
                  <div style={{ marginBottom: '1rem', position: 'relative', display: 'inline-block' }}>
                    <img 
                      src={formData.image} 
                      alt="Item preview"
                      style={{ 
                        width: '150px', 
                        height: '150px', 
                        objectFit: 'cover', 
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image: '' })}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: '#e44258',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="input"
                  style={{ padding: '0.5rem' }}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  Upload an image (max 5MB, JPG, PNG, GIF)
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={resetForm} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Update' : 'Add'} Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '400px',
            padding: '1.5rem'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.5rem' }}>Add Category</h2>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Category Name</label>
              <input
                type="text"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="input"
                placeholder="e.g., Linens"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Color</label>
              <input
                type="color"
                value={categoryForm.color}
                onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                className="input"
                style={{ height: '50px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowCategoryModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
              <button onClick={addCategory} className="btn btn-primary" style={{ flex: 1 }}>
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      {showLocationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '400px',
            padding: '1.5rem'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.5rem' }}>Add Location</h2>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Location Name</label>
              <input
                type="text"
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                className="input"
                placeholder="e.g., Storage Room B"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Address/Description</label>
              <input
                type="text"
                value={locationForm.address}
                onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                className="input"
                placeholder="e.g., Building 2, Floor 3"
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowLocationModal(false)} className="btn btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
              <button onClick={addLocation} className="btn btn-primary" style={{ flex: 1 }}>
                Add Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
